import { useAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';

import { useUserController } from 'api/controllers/UserController';

import { useCompanies } from 'context/CompanyContext';
import { usePermissions } from 'context/PermissionsContext';

import { useRouteSecurity } from 'hooks/useRouteSecurity';

import { INITIAL_INVOICES_FILTERS } from 'utils/constants/invoices';
import { INITIAL_PAGINATION_PROPS } from 'utils/constants/paginations';
import { INITIAL_REVENUE_FILTERS } from 'utils/constants/revenue';
import { HttpStatusCodes } from 'utils/enums/HttpStatusCodes';
import { mapPermissions } from 'utils/mappers/permissions';
import { AppRoutesEnum } from 'utils/routes';

import {
  invoicesFiltersAtom,
  invoicesPaginationAtom,
  areFiltrsVisibleAtom,
  revenueFiltersAtom,
  userAtom
} from 'state/state';

interface ProtectedRouteProps {
  readonly children: React.ReactNode;
  readonly path: AppRoutesEnum;
}

export const ProtectedRoute = ({ children, path }: ProtectedRouteProps) => {
  const { updatePermissions } = usePermissions();
  const { userLogin } = useUserController();
  const { getHomePageRoute, checkRoutePermission } = useRouteSecurity();
  const { companiesList } = useCompanies();
  const { companyId } = useParams();
  const location = useLocation();

  const prevCompanyId = useRef<string | undefined>(undefined);
  const prevPath = useRef<string>('');

  const [user, setUser] = useAtom(userAtom);
  const [invoiceFilters, setInvoiceFilters] = useAtom(invoicesFiltersAtom);
  const [revenueFilters, setRevenueFilters] = useAtom(revenueFiltersAtom);

  const [pagination, setPagination] = useAtom(invoicesPaginationAtom);
  const [areFiltersVisible, setAreFiltersVisible] =
    useAtom(areFiltrsVisibleAtom);

  const updateLoginData = useCallback(async () => {
    const result = await userLogin();
    updatePermissions(mapPermissions(result.permissions));
    setUser(result);
  }, []);

  // TODO: Refactor this useEffect
  useEffect(() => {
    if (
      path === AppRoutesEnum.HOME &&
      Number(location.state?.reason) === HttpStatusCodes.Forbidden
    ) {
      updateLoginData();
    }

    // Check if navigating away from invoice-related pages
    const isNavigatingToInvoiceDetail =
      path === AppRoutesEnum.INVOICE_VERIFICATION ||
      path === AppRoutesEnum.COMPANY_INVOICES ||
      path === AppRoutesEnum.INVOICES ||
      path === AppRoutesEnum.INVOICES_ACCOUNTING ||
      path === AppRoutesEnum.INVOICES_APPROVAL ||
      path === AppRoutesEnum.INVOICES_PAYMENT ||
      path === AppRoutesEnum.COMPANY_INVOICES_ACCOUNTING ||
      path === AppRoutesEnum.COMPANY_INVOICES_APPROVAL ||
      path === AppRoutesEnum.COMPANY_INVOICES_PAYMENT;

    const isNavigatingToRevenue =
      path === AppRoutesEnum.REVENUE ||
      path === AppRoutesEnum.COMPANY_REVENUE ||
      path === AppRoutesEnum.REVENUE_VERIFICATION;

    const wasOnRevenue =
      prevPath.current === AppRoutesEnum.REVENUE ||
      prevPath.current === AppRoutesEnum.COMPANY_REVENUE ||
      prevPath.current === AppRoutesEnum.REVENUE_VERIFICATION;

    if (wasOnRevenue && !isNavigatingToRevenue) {
      setInvoiceFilters(INITIAL_INVOICES_FILTERS);
    } else if (!wasOnRevenue && isNavigatingToRevenue) {
      setRevenueFilters(INITIAL_REVENUE_FILTERS);
    }

    const isOneIdUndefined =
      (prevCompanyId.current && !companyId) ||
      (!prevCompanyId.current && companyId);

    if (!isOneIdUndefined && prevCompanyId.current !== companyId) {
      if (isNavigatingToRevenue) {
        setRevenueFilters(INITIAL_REVENUE_FILTERS);
      } else {
        setInvoiceFilters(INITIAL_INVOICES_FILTERS);
      }
      setPagination(INITIAL_PAGINATION_PROPS);
      setAreFiltersVisible(false);
    }

    prevCompanyId.current = companyId as string | undefined;
    prevPath.current = path;

    if (!isNavigatingToInvoiceDetail) {
      setPagination(INITIAL_PAGINATION_PROPS);
      setAreFiltersVisible(false);
    }
  }, [
    path,
    location,
    companyId,
    setInvoiceFilters,
    setRevenueFilters,
    setPagination,
    setAreFiltersVisible,
    updateLoginData
  ]);

  const hasRoutePermission = useMemo(
    () => checkRoutePermission(path),
    [path, checkRoutePermission]
  );

  const companyExists = useMemo(
    () =>
      !companyId ||
      companiesList.some((company) => company.id === Number(companyId)),
    [companiesList, companyId]
  );

  if (!hasRoutePermission || !companyExists) {
    return <Navigate to={getHomePageRoute()} />;
  }

  return children;
};
